import React from "react";
import { Avatar } from "@outpave/ds";
import { logoLink } from "components/common/RenderMerchantDisplay";

// American Express before Amex so the longer form wins. See README.md.
const NETWORKS: { pattern: RegExp; label: string; domain: string }[] = [
  {
    pattern: /american\s*express/i,
    label: "American Express",
    domain: "americanexpress.com",
  },
  { pattern: /\bamex\b/i, label: "Amex", domain: "americanexpress.com" },
  { pattern: /\bvisa\b/i, label: "Visa", domain: "visa.com" },
  { pattern: /\bmastercard\b/i, label: "Mastercard", domain: "mastercard.com" },
];

export type CardNetworkMarkSize = "sm" | "md";

/** Rendered avatar width in px, so the logo is requested at the right scale. */
const SIZE_PX: Record<CardNetworkMarkSize, number> = { sm: 24, md: 28 };

/** Reads the card network out of a connection name. Returns null when unrecognised. */
export const readCardNetwork = (name: string): string | null =>
  NETWORKS.find(({ pattern }) => pattern.test(name))?.label ?? null;

export interface CardNetworkMarkProps {
  /** The connection name to read the network from, e.g. "Visa - JPMorgan Chase". */
  name: string;
  size?: CardNetworkMarkSize;
}

/** The card network's brand logo, falling back to a monogram. See README.md. */
const CardNetworkMark: React.FC<CardNetworkMarkProps> = ({
  name,
  size = "sm",
}) => {
  const network = NETWORKS.find(({ pattern }) => pattern.test(name));

  return (
    <Avatar
      size={size}
      // 2x the rendered width so the mark stays sharp on retina.
      src={
        network
          ? logoLink(
              network.domain,
              "icon",
              "lettermark",
              "light",
              SIZE_PX[size] * 2,
            )
          : undefined
      }
      name={network?.label ?? name}
      alt={network?.label ?? name}
    />
  );
};

export default CardNetworkMark;
